import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { Box } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import CustomerRegistration from './components/CustomerRegistration';
import Login from './components/Login';
import PlanSelection from './components/PlanSelection';
import Signup from './components/Signup';
import PaymentPage from './components/PaymentPage';
import PaymentSuccess from './components/PaymentSuccess';
import LovedOneSetup from './components/LovedOneSetup';
import CareCircleSetup from './components/CareCircleSetup';
import PaymentSummary from './components/PaymentSummary';
import EditLovedOne from './components/EditLovedOne';
import EditCareCircle from './components/EditCareCircle';
import Dashboard from './components/Dashboard';
import PaymentSetup from './components/PaymentSetup';
import MonitoringDashboard from './components/MonitoringDashboard';
import ScheduleManagement from './components/ScheduleManagement';
import CaregiverCircleManagement from './components/CaregiverCircleManagement';
import EnhancedDashboard from './components/EnhancedDashboard';
import ReportGenerator from './components/ReportGenerator';
import AdminDashboard from './components/AdminDashboard';
import AnalyticsDashboard from './components/AnalyticsDashboard';
import AdvancedAlertsDashboard from './components/AdvancedAlertsDashboard';
import AdvancedReportsDashboard from './components/AdvancedReportsDashboard';
import PerformanceDashboard from './components/PerformanceDashboard';
import EnhancedVacationDashboard from './components/EnhancedVacationDashboard';
import MyLovedOnes from './components/MyLovedOnes';
import UserProfile from './components/UserProfile';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import LandingPage from './components/LandingPage';
import HowItWorks from './components/HowItWorks';
import Pricing from './components/Pricing';
import { AuthProvider } from './contexts/AuthContext';
import { RoleProvider } from './contexts/RoleContext';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#2E7D32',
    },
    secondary: {
      main: '#1976d2',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  spacing: 4, // Reduce default spacing from 8 to 4
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        },
      },
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <RoleProvider>
          <Router>
          {/* Public Routes */}
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<PlanSelection />} />
            <Route path="/signup/account" element={<Signup />} />
            <Route path="/signup/payment" element={<PaymentPage />} />
            <Route path="/signup/payment-success" element={<PaymentSuccess />} />
            <Route path="/signup/loved-one" element={<LovedOneSetup />} />
            <Route path="/signup/care-circle" element={<CareCircleSetup />} />
            <Route path="/signup/payment-summary" element={<PaymentSummary />} />
          </Routes>

          {/* Protected Routes with Layout */}
          <Routes>
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Box sx={{ display: 'flex' }}>
                    <Header />
                    <Sidebar />
                    <Box component="main" sx={{ 
                      flexGrow: 1, 
                      marginLeft: '240px', 
                      marginTop: '64px',
                      padding: '0px'
                    }}>
                      <Dashboard />
                    </Box>
                  </Box>
                </ProtectedRoute>
              }
            />
            <Route
              path="/customer-registration"
              element={
                <ProtectedRoute>
                  <div style={{ 
                    display: 'flex', 
                    minHeight: '100vh',
                    fontFamily: 'Arial, sans-serif'
                  }}>
                    <Header />
                    <Sidebar />
                    <main style={{
                      flex: '1 1 auto',
                      marginLeft: '240px',
                      marginTop: '64px',
                      padding: '0',
                      backgroundColor: '#ffffff',
                      minHeight: 'calc(100vh - 64px)',
                      width: 'calc(100vw - 240px)',
                      boxSizing: 'border-box',
                      overflowX: 'auto'
                    }}>
                      <CustomerRegistration />
                    </main>
                  </div>
                </ProtectedRoute>
              }
            />
            <Route
              path="/payments"
              element={
                <ProtectedRoute>
                  <Box sx={{ display: 'flex' }}>
                    <Header />
                    <Sidebar />
                    <Box component="main" sx={{ 
                      flexGrow: 1, 
                      marginLeft: '240px', 
                      marginTop: '64px',
                      padding: '5px'
                    }}>
                      <PaymentSetup />
                    </Box>
                  </Box>
                </ProtectedRoute>
              }
            />
            <Route
              path="/monitoring"
              element={
                <AdminRoute>
                  <Box sx={{ display: 'flex' }}>
                    <Header />
                    <Sidebar />
                    <Box component="main" sx={{ 
                      flexGrow: 1, 
                      marginLeft: '240px', 
                      marginTop: '64px',
                      padding: '5px'
                    }}>
                      <MonitoringDashboard />
                    </Box>
                  </Box>
                </AdminRoute>
              }
            />
            <Route
              path="/schedules"
              element={
                <ProtectedRoute>
                  <Box sx={{ display: 'flex' }}>
                    <Header />
                    <Sidebar />
                    <Box component="main" sx={{ 
                      flexGrow: 1, 
                      marginLeft: '240px', 
                      marginTop: '64px',
                      padding: '5px'
                    }}>
                      <ScheduleManagement />
                    </Box>
                  </Box>
                </ProtectedRoute>
              }
            />
            <Route
              path="/caregiver-circles"
              element={
                <ProtectedRoute>
                  <Box sx={{ display: 'flex' }}>
                    <Header />
                    <Sidebar />
                    <Box component="main" sx={{ 
                      flexGrow: 1, 
                      marginLeft: '240px', 
                      marginTop: '64px',
                      padding: '5px'
                    }}>
                      <CaregiverCircleManagement />
                    </Box>
                  </Box>
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-loved-ones"
              element={
                <ProtectedRoute>
                  <Box sx={{ display: 'flex' }}>
                    <Header />
                    <Sidebar />
                    <Box component="main" sx={{ 
                      flexGrow: 1, 
                      marginLeft: '240px', 
                      marginTop: '64px',
                      padding: '5px'
                    }}>
                      <MyLovedOnes />
                    </Box>
                  </Box>
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-profile"
              element={
                <ProtectedRoute>
                  <Box sx={{ display: 'flex' }}>
                    <Header />
                    <Sidebar />
                    <Box component="main" sx={{
                      flexGrow: 1,
                      marginLeft: '240px',
                      marginTop: '64px',
                      padding: '5px'
                    }}>
                      <UserProfile />
                    </Box>
                  </Box>
                </ProtectedRoute>
              }
            />
            <Route
              path="/care-circle"
              element={
                <ProtectedRoute>
                  <Box sx={{ display: 'flex' }}>
                    <Header />
                    <Sidebar />
                    <Box component="main" sx={{
                      flexGrow: 1,
                      marginLeft: '240px',
                      marginTop: '64px',
                      padding: '5px'
                    }}>
                      <CaregiverCircleManagement />
                    </Box>
                  </Box>
                </ProtectedRoute>
              }
            />
            <Route
              path="/enhanced-dashboard"
              element={
                <ProtectedRoute>
                  <Box sx={{ display: 'flex' }}>
                    <Header />
                    <Sidebar />
                    <Box component="main" sx={{ 
                      flexGrow: 1, 
                      marginLeft: '240px', 
                      marginTop: '64px',
                      padding: '5px'
                    }}>
                      <EnhancedDashboard />
                    </Box>
                  </Box>
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports"
              element={
                <AdminRoute>
                  <Box sx={{ display: 'flex' }}>
                    <Header />
                    <Sidebar />
                    <Box component="main" sx={{ 
                      flexGrow: 1, 
                      marginLeft: '240px', 
                      marginTop: '64px',
                      padding: '5px'
                    }}>
                      <ReportGenerator />
                    </Box>
                  </Box>
                </AdminRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <Box sx={{ display: 'flex' }}>
                    <Header />
                    <Sidebar />
                    <Box component="main" sx={{ 
                      flexGrow: 1, 
                      marginLeft: '240px', 
                      marginTop: '64px',
                      padding: '5px'
                      }}>
                      <AdminDashboard />
                    </Box>
                  </Box>
                </ProtectedRoute>
              }
            />
            <Route
              path="/analytics"
              element={
                <ProtectedRoute>
                  <Box sx={{ display: 'flex' }}>
                    <Header />
                    <Sidebar />
                    <Box component="main" sx={{ 
                      flexGrow: 1, 
                      marginLeft: '240px', 
                      marginTop: '64px',
                      padding: '5px'
                    }}>
                      <AnalyticsDashboard />
                    </Box>
                  </Box>
                </ProtectedRoute>
              }
            />
            <Route
              path="/advanced-alerts"
              element={
                <ProtectedRoute>
                  <Box sx={{ display: 'flex' }}>
                    <Header />
                    <Sidebar />
                    <Box component="main" sx={{ 
                      flexGrow: 1, 
                      marginLeft: '240px', 
                      marginTop: '64px',
                      padding: '5px'
                    }}>
                      <AdvancedAlertsDashboard />
                    </Box>
                  </Box>
                </ProtectedRoute>
              }
            />
            <Route
              path="/advanced-reports"
              element={
                <ProtectedRoute>
                  <Box sx={{ display: 'flex' }}>
                    <Header />
                    <Sidebar />
                    <Box component="main" sx={{ 
                      flexGrow: 1, 
                      marginLeft: '240px', 
                      marginTop: '64px',
                      padding: '5px'
                    }}>
                      <AdvancedReportsDashboard />
                    </Box>
                  </Box>
                </ProtectedRoute>
              }
            />
            <Route
              path="/performance"
              element={
                <ProtectedRoute>
                  <Box sx={{ display: 'flex' }}>
                    <Header />
                    <Sidebar />
                    <Box component="main" sx={{ 
                      flexGrow: 1, 
                      marginLeft: '240px', 
                      marginTop: '64px',
                      padding: '5px'
                    }}>
                      <PerformanceDashboard />
                    </Box>
                  </Box>
                </ProtectedRoute>
              }
            />
            <Route
              path="/enhanced-vacation"
              element={
                <ProtectedRoute>
                  <Box sx={{ display: 'flex' }}>
                    <Header />
                    <Sidebar />
                    <Box component="main" sx={{ 
                      flexGrow: 1, 
                      marginLeft: '240px', 
                      marginTop: '64px',
                      padding: '5px'
                    }}>
                      <EnhancedVacationDashboard />
                    </Box>
                  </Box>
                </ProtectedRoute>
              }
            />
            <Route
              path="/edit-loved-one"
              element={
                <ProtectedRoute>
                  <Box sx={{ display: 'flex' }}>
                    <Header />
                    <Sidebar />
                    <Box component="main" sx={{ 
                      flexGrow: 1, 
                      marginLeft: '240px', 
                      marginTop: '64px',
                      padding: '5px'
                    }}>
                      <EditLovedOne />
                    </Box>
                  </Box>
                </ProtectedRoute>
              }
            />
            <Route
              path="/edit-care-circle"
              element={
                <ProtectedRoute>
                  <Box sx={{ display: 'flex' }}>
                    <Header />
                    <Sidebar />
                    <Box component="main" sx={{ 
                      flexGrow: 1, 
                      marginLeft: '240px', 
                      marginTop: '64px',
                      padding: '5px'
                    }}>
                      <EditCareCircle />
                    </Box>
                  </Box>
                </ProtectedRoute>
              }
            />
          </Routes>
        </Router>
        </RoleProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
